import React, { FC, useCallback, useState, useEffect } from 'react';
import { Col, Row, Modal, Form, ListGroup, ListGroupItem } from 'react-bootstrap';
import { createUseStyles } from 'react-jss';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { AlertBar, Button, ButtonVariant, Icon, Typography } from '@packages/ui/shared';
import { patchQmr, useQmrDispatch, useQmrReviewState, useQmrState, useQmrInfoRequests } from '@packages/contexts/qmrs';
import { useAuthState } from '@packages/contexts/auth';
import colors from '@packages/core/styles/colors';
import { Popover } from 'react-tiny-popover';
import { useAbortController } from '@packages/core/http';
import { analyticsService, qmrsService, httpClient } from '@web/services/singletons';
import { CvtExceptionStatus, CvtRepairDisposition, DsqmReviewStatusId, QmrStatusId } from '@packages/models/api';
import OpenInfoRequestModal from './open-info-request-modal';
import { ANALYTICS_EVENTS } from '@packages/core/analytics';
import { useInvestigationModal } from '@packages/contexts/investigations';
import { useEscalationModal } from '@packages/contexts/escalations';
import { useFeatureFlags } from '@packages/contexts/feature-flags';
import { CustomDropdownButtonType, Dropdown } from './qmr-actions-dropdown';
import { getPopoverContentLocation } from '../../utils/popover-content-location';
import { qmrActions } from '@packages/contexts/qmrs';
import { CvtRepairDispositionModal } from './cvt-repair-disposition-modal';

const useQmrActionsStyles = createUseStyles({
    container: {
        borderBottom: `1px solid ${colors.grayThree}`,
        padding: `16px`,
    },
    actionsPopover: {
        minWidth: 335,
        backgroundColor: colors.white,
        border: `1px solid ${colors.grayThree}`,
        borderRadius: 4,
        boxShadow: `0px 8px 12px 0px rgba(0, 0, 0, 0.16)`,
    },
    popoverActionsSection: {
        padding: '4px 0',
    },
});

const QmrActions: FC<{
    onRestrictQmrSuccess: () => void;
    onUnrestrcitQmrSuccess: () => void;
    hideQMRId?: boolean;
    setQmrSuccessfullyEscalated: React.Dispatch<React.SetStateAction<boolean>>;
    setQmrAddedToInvestigationSuccessfully: React.Dispatch<React.SetStateAction<string>>;
}> = ({
    onRestrictQmrSuccess,
    onUnrestrcitQmrSuccess,
    hideQMRId = false,
    setQmrSuccessfullyEscalated,
    setQmrAddedToInvestigationSuccessfully,
}) => {
    const classes = useQmrActionsStyles();
    const { abortSignalRef } = useAbortController();
    const { t } = useTranslation();
    const { qmr } = useQmrState();
    const qmrDispatch = useQmrDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { isNqrCvtDiagnosticsFeatureEnabled } = useFeatureFlags();
    const {
        updatedStatusId,
        updatedStatusValue,
        availableActions,
        primaryActionDescription,
        showPrimaryActionButton,
        primaryActionButtonVariant,
        hasManyPrimaryActions,
    } = useQmrReviewState();
    const auth = useAuthState();
    const { hasOpenInfoRequest } = useQmrInfoRequests({ qmrsService });

    const [verticalDotsMenuExpanded, setVerticalDotsMenuExpanded] = useState(false);
    const [showActionsModal, setShowActionsModal] = useState(false);
    const [updatingRestrictedStatus, setUpdatingRestrictedStatus] = useState(false);
    const [showOpenInfoReqModal, setShowOpenInfoReqModal] = useState(false);
    const [primaryActionLoader, setPrimaryActionLoader] = useState(false);
    const [showNoActionNeededInfoModal, setShowNoActionNeededInfoModal] = useState(false);
    const [showCvtRepairDispositionModal, setShowCvtRepairDispositionModal] = useState<boolean>(false);
    const [isApprovingCvtRepairDisposition, setIsApprovingCvtRepairDisposition] = useState<boolean>(false);

    const { showModal } = useInvestigationModal();
    const [qmrSuccessfullyAddedToInvestigation, setQmrSuccessfullyAddedToInvestigation] = useState('');

    const setQmrFeedbackDrawerVisible = useCallback(
        (isVisible: boolean) => {
            qmrDispatch(qmrActions.setQmrFeedbackDrawerVisible(isVisible));
        },
        [qmrDispatch]
    );

    const setQmrFeedbackDrawerCollapsed = useCallback(
        (isCollapsed: boolean) => {
            qmrDispatch(qmrActions.setQmrFeedbackDrawerCollapsed(isCollapsed));
        },
        [qmrDispatch]
    );

    const { showModal: showEscalationModal } = useEscalationModal();

    const showVerticalDotsButton =
        !!qmr?.capabilities.editQmr ||
        qmr?.capabilities.giveFeedbackOnQmr ||
        qmr?.capabilities.markQmrAsRestricted ||
        auth.account?.systemCapabilities.canExportPDF;

    const transitionQmrStatus = useCallback(
        (newStatusValue = updatedStatusValue) => {
            if (!qmr || !updatedStatusId || primaryActionLoader) {
                return;
            }
            setPrimaryActionLoader(true);
            patchQmr({
                qmrId: qmr.qmrId,
                qmrDispatch,
                qmrsService,
                signal: abortSignalRef.current,
                qmrPatch: {
                    [updatedStatusId]: newStatusValue,
                },
            })
                .then((updatedQmr) => {
                    if (!updatedQmr) {
                        return;
                    }

                    analyticsService.logEvent(ANALYTICS_EVENTS.USER_CHANGES_QMR_STATUS, {
                        tsId: updatedQmr.stsIdentifier,
                        previousQaStatus: qmr.qmrStatus.qmrStatusId,
                        newQaStatus: updatedQmr.qmrStatus.qmrStatusId,
                        previousDsqmStatus: qmr.dsqmReviewStatus.dsqmReviewStatusId,
                        newDsqmStatus: updatedQmr.dsqmReviewStatus.dsqmReviewStatusId,
                    });

                    showActionsModal && setShowActionsModal(false);

                    if (updatedQmr.capabilities.giveFeedbackOnQmr) {
                        // more info request status should show another modal first
                        if (
                            updatedStatusValue !== QmrStatusId.MoreInfoRequested &&
                            updatedStatusValue !== DsqmReviewStatusId.MoreInfoRequested
                        ) {
                            setQmrFeedbackDrawerVisible(true);
                        }
                    }

                    if (updatedQmr.capabilities.openCloseMoreInfoRequest) {
                        if (
                            updatedStatusValue === QmrStatusId.MoreInfoRequested ||
                            updatedStatusValue === DsqmReviewStatusId.MoreInfoRequested
                        ) {
                            if (hasOpenInfoRequest) {
                                navigate(`${location.pathname}?tab=INFO`, {
                                    // search: '?tab=INFO',
                                    replace: true,
                                });
                            } else {
                                setShowOpenInfoReqModal(true);
                            }
                        }
                    }

                    if (
                        !updatedQmr.capabilities.giveFeedbackOnQmr &&
                        !updatedQmr.capabilities.openCloseMoreInfoRequest
                    ) {
                        window.location.reload();
                    }
                    setPrimaryActionLoader(false);
                })
                .catch((e) => {
                    alert(e.message);
                    setPrimaryActionLoader(false);
                });
        },
        [
            abortSignalRef,
            hasOpenInfoRequest,
            location.pathname,
            navigate,
            primaryActionLoader,
            qmr,
            qmrDispatch,
            showActionsModal,
            updatedStatusId,
            updatedStatusValue,
        ]
    );

    const toggleRestrictedStatus = useCallback(() => {
        if (!qmr || updatingRestrictedStatus) {
            return;
        }

        setUpdatingRestrictedStatus(true);

        patchQmr({
            qmrId: qmr.qmrId,
            qmrsService,
            qmrDispatch,
            signal: abortSignalRef.current,
            qmrPatch: {
                restricted: !qmr.restricted,
            },
        })
            .then((updatedQmr) => {
                if (!updatedQmr) {
                    return;
                }

                if (qmr.restricted) {
                    onUnrestrcitQmrSuccess();
                } else {
                    onRestrictQmrSuccess();
                }

                setUpdatingRestrictedStatus(false);
                setVerticalDotsMenuExpanded(false);
            })
            .catch((e) => {
                setUpdatingRestrictedStatus(false);
                setVerticalDotsMenuExpanded(false);
                alert(e.message);
            });
    }, [abortSignalRef, onRestrictQmrSuccess, onUnrestrcitQmrSuccess, qmr, qmrDispatch, updatingRestrictedStatus]);

    function handleCvtRepairDispositionModalApprove(repairDisposition: CvtRepairDisposition) {
        if (!repairDisposition || !qmr) {
            return;
        }

        setIsApprovingCvtRepairDisposition(true);

        qmrsService
            .postCvtRepairDispositionResult({
                qmrId: qmr.qmrId,
                repairDisposition,
                signal: abortSignalRef.current,
            })
            .then((response) => {
                if (!response.success && response.aborted) {
                    return;
                } else if (!response.success) {
                    throw response.data;
                }

                setShowCvtRepairDispositionModal(false);
                transitionQmrStatus(DsqmReviewStatusId.Approved);
            })
            .catch((e) => {
                alert(e.message);
                setIsApprovingCvtRepairDisposition(false);
            });
    }

    function handleCvtRepairDispositionModalExited() {
        setIsApprovingCvtRepairDisposition(false);
    }

    function handlePrimaryActionPress(key: string) {
        if (!qmr) {
            return;
        }

        const selectedStatusId = availableActions[Number(key)].statusId;

        if (selectedStatusId === QmrStatusId.NoActionNeeded && qmr.isNqr) {
            setShowNoActionNeededInfoModal(true);
            return;
        }

        const isNqrApproval =
            qmr.isNqr && updatedStatusId === 'dsqmReviewStatusId' && selectedStatusId === DsqmReviewStatusId.Approved;
        const isRecommendationChallengeSubmitted =
            qmr.cvtChallengeDetails?.cvtExceptionStatus === CvtExceptionStatus.RecommendationChallenged;

        if (isNqrCvtDiagnosticsFeatureEnabled && isNqrApproval && isRecommendationChallengeSubmitted) {
            setShowCvtRepairDispositionModal(true);
            return;
        }

        transitionQmrStatus(selectedStatusId);
    }

    const renderPrimaryActionButton = () => {
        if (hasManyPrimaryActions) {
            const dropdownOptions = availableActions.map((option, index) => {
                return {
                    id: index.toString(),
                    label: option.label,
                    disabled: option.isSelected,
                };
            });
            return (
                <Dropdown
                    label={primaryActionDescription}
                    options={dropdownOptions}
                    onPress={handlePrimaryActionPress}
                    customDropdownButton={
                        primaryActionButtonVariant !== 'primary-dropdown'
                            ? {
                                  type: CustomDropdownButtonType.primary,
                                  variant: primaryActionButtonVariant as ButtonVariant,
                              }
                            : undefined
                    }
                    isLoading={primaryActionLoader}
                />
            );
        }
        return (
            <Button
                isLoading={primaryActionLoader}
                variant={primaryActionButtonVariant as ButtonVariant}
                onPress={() => {
                    // if (hasManyPrimaryActions) {
                    //     setShowActionsModal(true);
                    //     return;
                    // }

                    transitionQmrStatus();
                }}
            >
                {primaryActionDescription}
            </Button>
        );
    };

    useEffect(() => {
        setQmrSuccessfullyEscalated(false);
    }, [qmr]);

    const onClickDownloadButton = (e: any) => {
        e.preventDefault();
        e.stopPropagation();
        const options = {
            headers: {
                'X-Subcomm-Access-Token': auth.token,
            },
        };
        let filename = '';
        fetch(`${httpClient.baseUrl}/qmrs/${qmr?.qmrId}/qmr.pdf`, options)
            .then((res) => {
                let contentDispositionHeader = res.headers.get('Content-Disposition');
                if (contentDispositionHeader) {
                    let filenameSplited = contentDispositionHeader.split('filename=');
                    if (filenameSplited && filenameSplited.length > 1) {
                        filename = filenameSplited[1].split(';')[0].replaceAll('"', '');
                    }
                }
                return res.blob();
            })
            .then((blob) => {
                const file = window.URL.createObjectURL(blob);
                if (filename) {
                    let a = document.createElement('a');
                    document.body.appendChild(a);
                    a.setAttribute('style', 'display: none');
                    a.href = file;
                    a.download = filename;
                    a.click();
                    window.URL.revokeObjectURL(file);
                } else {
                    window.location.assign(file);
                }
            });
    };

    return (
        <>
            <Modal
                size="lg"
                centered
                show={showNoActionNeededInfoModal}
                onHide={() => {
                    setShowNoActionNeededInfoModal(false);
                }}
            >
                <Modal.Header style={{ fontWeight: 'bold' }}>{t('modals:noActionNeededWarning.title')}</Modal.Header>
                <Modal.Body>
                    <Trans i18nKey="modals:noActionNeededWarning.message" components={{ bold: <strong /> }} />
                    <ListGroup>
                        <ListGroupItem style={{ border: 'none', flexDirection: 'row' }}>
                            <Typography>{'\u2022'}</Typography>
                            <Typography style={{ paddingLeft: 5 }}>
                                {t('modals:noActionNeededWarning.point1')}
                            </Typography>
                        </ListGroupItem>
                        <ListGroupItem style={{ border: 'none', flexDirection: 'row' }}>
                            <Typography>{'\u2022'}</Typography>
                            <Typography style={{ paddingLeft: 5 }}>
                                {t('modals:noActionNeededWarning.point2')}
                            </Typography>
                        </ListGroupItem>
                    </ListGroup>
                    <div style={{ paddingTop: 15 }}>
                        <Typography>{t('modals:noActionNeededWarning.question')}</Typography>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="ghost-blue"
                        onPress={() => {
                            setShowNoActionNeededInfoModal(false);
                        }}
                    >
                        {t('modals:noActionNeededWarning.cancel', 'Cancel')}
                    </Button>
                    <Button
                        variant="primary"
                        onPress={() => {
                            transitionQmrStatus(QmrStatusId.NoActionNeeded);
                            setShowNoActionNeededInfoModal(false);
                        }}
                    >
                        {t('modals:noActionNeededWarning.confirm', 'Confirm')}
                    </Button>
                </Modal.Footer>
            </Modal>
            <Modal
                centered
                show={showActionsModal}
                onHide={() => {
                    setShowActionsModal(false);
                }}
            >
                <Modal.Header closeButton>{t('modals:qmrActions.title', 'Choose the next step')}</Modal.Header>

                <Modal.Body>
                    {availableActions.map((action, index) => {
                        return (
                            <Form.Check
                                key={index}
                                className="mb-3"
                                label={action.label}
                                type="radio"
                                id={action.label}
                                checked={action.isSelected}
                                onChange={action.onSelect}
                            />
                        );
                    })}
                </Modal.Body>

                <Modal.Footer>
                    <Button
                        disabled={!updatedStatusValue}
                        title={t('modals:qmrActions.actions.continue', 'Continue')}
                        onPress={() => {
                            transitionQmrStatus();
                        }}
                    />
                </Modal.Footer>
            </Modal>

            <OpenInfoRequestModal
                show={showOpenInfoReqModal}
                onHide={() => setShowOpenInfoReqModal(false)}
                onSuccess={() => qmr != null && qmr.capabilities.giveFeedbackOnQmr && setQmrFeedbackDrawerVisible(true)}
            />
            <CvtRepairDispositionModal
                isApproving={isApprovingCvtRepairDisposition}
                onApprove={handleCvtRepairDispositionModalApprove}
                onExited={handleCvtRepairDispositionModalExited}
                onHide={() => setShowCvtRepairDispositionModal(false)}
                show={showCvtRepairDispositionModal}
            />
            <section className={classes.container}>
                <Row>
                    <Col>
                        <div className="d-flex align-items-center justify-content-between">
                            {!hideQMRId && (
                                <div className="d-flex flex-column">
                                    <div>
                                        <Link
                                            to={{
                                                pathname: '/qmrs',
                                                search: location.state?.listQueryParams
                                                    ? '?' + location.state.listQueryParams
                                                    : undefined,
                                            }}
                                        >
                                            <Typography color="blueOne">QMRs</Typography>
                                        </Link>

                                        <Typography style={{ paddingHorizontal: 8 }} color="textDarkSecondary">
                                            /
                                        </Typography>

                                        <Typography color="textDarkSecondary">{qmr?.displayIdentifier}</Typography>
                                    </div>

                                    <Typography variant="h5">Report</Typography>
                                </div>
                            )}
                            <div className="d-flex">
                                {!!showPrimaryActionButton && renderPrimaryActionButton()}

                                <div className="mx-4">
                                    <a
                                        href={
                                            qmr
                                                ? `mailto:?body=${encodeURIComponent(
                                                      qmr.shareUrl
                                                  )}&subject=${encodeURIComponent(qmr.displayIdentifier)}`
                                                : ''
                                        }
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ textDecoration: 'none' }}
                                    >
                                        <Button variant="tertiary">
                                            <Icon name="share" />
                                        </Button>
                                    </a>
                                </div>

                                {showVerticalDotsButton && (
                                    <Popover
                                        isOpen={verticalDotsMenuExpanded}
                                        positions={['bottom', 'top']}
                                        onClickOutside={() => setVerticalDotsMenuExpanded(false)}
                                        contentLocation={({ childRect, popoverRect, position }) => {
                                            return getPopoverContentLocation(childRect, popoverRect, position, 4);
                                        }}
                                        containerClassName={classes.actionsPopover}
                                        content={
                                            <div className={classes.popoverActionsSection}>
                                                {auth.account?.systemCapabilities.canExportPDF && (
                                                    <a href="javascript:void(0);" style={{ textDecoration: 'none' }}>
                                                        <Button
                                                            alignTitle="left"
                                                            variant="ghost-gray"
                                                            onPress={(e: any) => {
                                                                setVerticalDotsMenuExpanded(false);
                                                                onClickDownloadButton(e);
                                                            }}
                                                        >
                                                            <Icon size={14} name="download" />
                                                            <Typography
                                                                style={{ paddingLeft: 12 }}
                                                                variant="labelRegular"
                                                            >
                                                                {t('buttons:download', 'Download') + ' PDF'}
                                                            </Typography>
                                                        </Button>
                                                    </a>
                                                )}
                                                {qmr?.capabilities.editQmr && (
                                                    <Button
                                                        alignTitle="left"
                                                        variant="ghost-gray"
                                                        onPress={() => {
                                                            setVerticalDotsMenuExpanded(false);
                                                            navigate(`/qmrs/${qmr?.displayIdentifier}/edit`, {
                                                                state: location.state,
                                                            });
                                                        }}
                                                    >
                                                        <Icon size={14} name="pencil" />
                                                        <Typography style={{ paddingLeft: 12 }} variant="labelRegular">
                                                            {t('qmrs:qmrDetail.actions.edit', 'Edit Information')}
                                                        </Typography>
                                                    </Button>
                                                )}
                                                {qmr?.capabilities.markQmrAsRestricted && (
                                                    <Button
                                                        alignTitle="left"
                                                        variant="ghost-gray"
                                                        isLoading={updatingRestrictedStatus}
                                                        onPress={toggleRestrictedStatus}
                                                    >
                                                        <Icon
                                                            size={14}
                                                            name={qmr.restricted ? 'lock-open' : 'lock-closed'}
                                                        />
                                                        <Typography style={{ paddingLeft: 12 }} variant="labelRegular">
                                                            {qmr.restricted
                                                                ? t(
                                                                      'qmrs:qmrDetail.actions.restrictQmr',
                                                                      'Mark Report Unrestricted'
                                                                  )
                                                                : t(
                                                                      'qmrs:qmrDetail.actions.unrestrictQmr',
                                                                      'Mark Report Restricted'
                                                                  )}
                                                        </Typography>
                                                    </Button>
                                                )}
                                                {qmr?.capabilities.giveFeedbackOnQmr && (
                                                    <Button
                                                        alignTitle="left"
                                                        variant="ghost-gray"
                                                        onPress={() => {
                                                            setVerticalDotsMenuExpanded(false);
                                                            setQmrFeedbackDrawerVisible(true);
                                                            setQmrFeedbackDrawerCollapsed(false);
                                                        }}
                                                    >
                                                        <Icon size={14} name="chat-bubble-text" />
                                                        <Typography style={{ paddingLeft: 12 }} variant="labelRegular">
                                                            {t('qmrs:qmrDetail.actions.giveFeedback', 'Give Feedback')}
                                                        </Typography>
                                                    </Button>
                                                )}
                                                {auth.account?.investigationCapabilities.editAnInvestigation && (
                                                    <Button
                                                        alignTitle="left"
                                                        variant="ghost-gray"
                                                        onPress={() => {
                                                            if (!qmr) {
                                                                return;
                                                            }

                                                            setVerticalDotsMenuExpanded(false);
                                                            showModal({
                                                                selectedQmrs: [qmr],
                                                                allowInvestigationReassignment: true,
                                                                onSuccess: (investigation) => {
                                                                    setQmrAddedToInvestigationSuccessfully(
                                                                        investigation.title
                                                                    );
                                                                },
                                                            });
                                                        }}
                                                    >
                                                        <Icon
                                                            size={14}
                                                            name={qmr?.investigationId ? 'plus' : 'folders'}
                                                        />
                                                        <Typography style={{ paddingLeft: 12 }} variant="labelRegular">
                                                            {qmr?.investigationId
                                                                ? t(
                                                                      'qmrs:qmrDetail.actions.moveToInvestigation',
                                                                      'Move to Investigation'
                                                                  )
                                                                : t(
                                                                      'qmrs:qmrDetail.actions.addToInvestigation',
                                                                      'Add to Investigation'
                                                                  )}
                                                        </Typography>
                                                    </Button>
                                                )}
                                                {qmr?.capabilities.canEscalateQmr && !qmr.isNqr && (
                                                    <Button
                                                        alignTitle="left"
                                                        variant="ghost-gray"
                                                        onPress={() => {
                                                            if (!qmr) {
                                                                return;
                                                            }
                                                            setVerticalDotsMenuExpanded(false);
                                                            if (qmr.escalated) {
                                                                return window.alert('The QMR is already Escalated');
                                                            }
                                                            showEscalationModal({
                                                                selectedQmrs: [qmr],
                                                                onSuccess: () => {
                                                                    setQmrSuccessfullyEscalated(true);
                                                                },
                                                            });
                                                        }}
                                                    >
                                                        <Icon size={14} name="escalation" />
                                                        <Typography style={{ paddingLeft: 12 }} variant="labelRegular">
                                                            {t('qmrs:qmrDetail.actions.escalate', 'Escalate')}
                                                        </Typography>
                                                    </Button>
                                                )}
                                            </div>
                                        }
                                    >
                                        <div>
                                            <Button
                                                active={verticalDotsMenuExpanded}
                                                variant="tertiary"
                                                onPress={() => setVerticalDotsMenuExpanded(!verticalDotsMenuExpanded)}
                                            >
                                                <Icon name="more-dots-vertical" />
                                            </Button>
                                        </div>
                                    </Popover>
                                )}
                            </div>
                        </div>
                    </Col>
                </Row>
            </section>
        </>
    );
};

export default QmrActions;
